import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import {
  HiOutlineXMark,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlinePencilSquare
} from 'react-icons/hi2';
import { User } from '../../../../../../../types';
import { toast } from 'react-toastify';
import { updateUserById } from '../../../../../../../../../../crud/users_api/users';
import { getRolesOptions, getPuestosOptions } from '../../modules/utils';
import { RoleOption, PuestoOption } from '../../types';
import './EditUser.scss';

const formatDateForInput = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(`${dateString}T00:00:00`);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const EditUser: React.FC<any> = ({
  showEditModal,
  setShowEditModal,
  setCurrentData,
  currentData,
  currentUser,
  originalUsers,
  setOriginalUsers
}) => {
  const [roles, setRoles] = useState<any[]>([]);
  const [puestos, setPuestos] = useState<any[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRol, setSelectedRol] = useState<RoleOption | null>(null);
  const [selectedPuesto, setSelectedPuesto] = useState<PuestoOption | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  useEffect(() => {
    setCurrentStep(1);
    setError(null);
    setShowPassword(false);

    if (currentUser) {
      setIsActive(currentUser.state);

      setFormValues({
        username: currentUser.username ?? '',
        middle_name: currentUser.middle_name ?? '',
        last_name: currentUser.last_name ?? '',
        dob: currentUser.dob ? formatDateForInput(currentUser.dob) : '',
        address: currentUser.address ?? '',
        email: currentUser.email ?? '',
        password: currentUser.password ?? '',
        phone_number: currentUser.phone_number ?? '',
      });

      if (currentUser.rol_id && roles.length > 0) {
        const selectedRole = roles.find((role: any) => role.id === currentUser.rol_id);
        if (selectedRole) {
          setSelectedRol({
            rol_id: selectedRole.id,
            rol_name: selectedRole.name,
            rol_description: selectedRole.description || ''
          });
        }
      }

      if (currentUser.puesto_id && puestos.length > 0) {
        const selectedPuestoObj = puestos.find((puesto: any) => puesto.id === currentUser.puesto_id);
        if (selectedPuestoObj) {
          setSelectedPuesto({
            puesto_id: selectedPuestoObj.id,
            puesto_name: selectedPuestoObj.name,
            puesto_description: selectedPuestoObj.description || ''
          });
        }
      }
    }
  }, [currentUser, roles, puestos, showEditModal]);

  useEffect(() => {
    getRolesOptions().then((response) => {
      if (response) setRoles(response);
    });
    getPuestosOptions().then((response) => {
      if (response) setPuestos(response);
    });
  }, []);

  const handleChange = (field: string, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep1 = (): boolean => {
    if (!formValues.email?.trim()) {
      setError('El email es requerido');
      return false;
    }
    if (!formValues.email.includes('@') || !formValues.email.includes('.')) {
      setError('El email no tiene un formato válido');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setError(null);
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    setError(null);
    setCurrentStep(1);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setShowEditModal(false);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setError(null);

    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;

    const current = currentUser || ({} as User);

    const currentNorm: Record<string, any> = {
      username: (current.username || '').toString().trim(),
      middle_name: current.middle_name ?? null,
      last_name: (current.last_name || '').toString().trim(),
      dob: current.dob ? formatDateForInput(current.dob) : '',
      address: (current.address || '').toString().trim(),
      email: (current.email || '').toString().trim(),
      phone_number: (current.phone_number || '').toString().trim(),
      password: current.password ? current.password.toString() : '',
    };

    const payload: Partial<User> = {};

    const fieldNames = [
      'username',
      'middle_name',
      'last_name',
      'dob',
      'address',
      'email',
      'password',
      'phone_number',
    ];

    for (const name of fieldNames) {
      let value: string | null = null;
      if (formValues && Object.prototype.hasOwnProperty.call(formValues, name)) {
        const fv = formValues[name];
        value = fv !== null && fv !== undefined ? String(fv).trim() : '';
      } else {
        continue;
      }

      if (name === 'middle_name') {
        const newVal = value === '' ? null : value;
        const cur = currentNorm.middle_name === null ? null : currentNorm.middle_name;
        if (String(newVal ?? '') !== String(cur ?? '')) {
          (payload as any)[name] = newVal;
        }
        continue;
      }

      if (name === 'password') {
        if (value !== '' && value !== (currentNorm.password || '')) {
          (payload as any)[name] = value;
        }
        continue;
      }

      const curComparable = currentNorm[name] ?? '';
      if (value !== String(curComparable)) {
        (payload as any)[name] = value === '' ? null : value;
      }
    }

    if (selectedRol && Number(selectedRol.rol_id) !== Number(current.rol_id ?? null)) {
      payload.rol_id = Number(selectedRol.rol_id);
    }
    if (selectedPuesto && Number(selectedPuesto.puesto_id) !== Number(current.puesto_id ?? null)) {
      payload.puesto_id = Number(selectedPuesto.puesto_id);
    }

    if (typeof isActive === 'boolean' && isActive !== (typeof current.state === 'boolean' ? current.state : !!current.state)) {
      payload.state = isActive;
    }

    if (Object.keys(payload).length === 0) {
      toast.info('No hay cambios para guardar.');
      setIsSubmitting(false);
      return;
    }

    const userId = Number(current.user_id);

    updateUserById(userId, payload as any)
      .then(() => {
        const updated_user: User = {
          ...current,
          ...payload,
          user_id: current.user_id,
          local_id: localId,
        } as User;

        const newOriginalUsers = originalUsers.map((u: User) =>
          u.user_id === updated_user.user_id ? updated_user : u
        );
        setCurrentData(newOriginalUsers);
        setOriginalUsers(newOriginalUsers);

        toast.success('Usuario Actualizado con Éxito!');
        setShowEditModal(false);
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message || 'Error al actualizar usuario';
        setError(typeof message === 'string' ? message : 'Error al actualizar usuario');
        toast.error(message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      show={showEditModal}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
      dialogClassName="mp-modal"
    >
      <div className="mp-modal-header">
        <div className="form-modal-title-container">
          <h5 className="form-modal-title">Actualizar Usuario</h5>
        </div>
        <button className="mp-modal-close" onClick={handleClose} disabled={isSubmitting}>
          <HiOutlineXMark size={18} />
        </button>
      </div>

      <div className="mp-modal-body">
        <div className="mp-step-indicator">
          <div className={`mp-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <span className="mp-step-number">1</span>
            <span>Información Personal</span>
          </div>
          <div className="mp-step-divider"></div>
          <div className={`mp-step ${currentStep === 2 ? 'active' : ''}`}>
            <span className="mp-step-number">2</span>
            <span>Rol y Puesto</span>
          </div>
        </div>

        {error && <div className="mp-form-error">{error}</div>}

        {currentStep === 1 && (
          <>
            <div className="mp-form-row">
              <div className="mp-form-group">
                <label className="mp-form-label">Nombre (Solo lectura)</label>
                <input className="mp-form-input" type="text" value={formValues.username ?? ''} readOnly />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Apellido (Solo lectura)</label>
                <input className="mp-form-input" type="text" value={formValues.last_name ?? ''} readOnly />
              </div>
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Correo Electrónico *</label>
              <input className="mp-form-input" type="email" value={formValues.email ?? ''} onChange={(e) => handleChange('email', e.target.value)} placeholder="correo@ejemplo.com" disabled={isSubmitting} />
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Contraseña</label>
              <div className="mp-form-password">
                <input className="mp-form-input" type={showPassword ? 'text' : 'password'} value={formValues.password ?? ''} onChange={(e) => handleChange('password', e.target.value)} placeholder="Dejar vacío para no cambiar" disabled={isSubmitting} />
                <button type="button" className="mp-form-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? <HiOutlineEyeSlash size={18} /> : <HiOutlineEye size={18} />}
                </button>
              </div>
            </div>

            <div className="mp-form-row">
              <div className="mp-form-group">
                <label className="mp-form-label">Teléfono</label>
                <input className="mp-form-input" type="tel" value={formValues.phone_number ?? ''} onChange={(e) => handleChange('phone_number', e.target.value)} placeholder="+56 9 1234 5678" disabled={isSubmitting} />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Fecha de Nacimiento (Solo lectura)</label>
                <input className="mp-form-input" type="text" value={formValues.dob ? formatDisplayDate(formValues.dob) : ''} readOnly />
              </div>
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Dirección</label>
              <input className="mp-form-input" type="text" value={formValues.address ?? ''} onChange={(e) => handleChange('address', e.target.value)} placeholder="Av. Principal 123" disabled={isSubmitting} />
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <div className="mp-form-group">
              <label className="mp-form-label">Rol</label>
              <div className="mp-form-chips">
                {roles.map((role: any) => (
                  <button key={role.id} type="button" className={`mp-form-chip ${selectedRol?.rol_id === role.id ? 'selected' : ''}`} onClick={() => setSelectedRol({ rol_id: role.id, rol_name: role.name, rol_description: role.description || '' })} disabled={isSubmitting}>
                    {role.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Puesto de Trabajo</label>
              {puestos.length === 0 ? (
                <p className="mp-form-error">No hay puestos de trabajo disponibles.</p>
              ) : (
                <div className="mp-form-chips">
                  {puestos.map((puesto: any) => (
                    <button key={puesto.id} type="button" className={`mp-form-chip ${selectedPuesto?.puesto_id === puesto.id ? 'selected' : ''}`} onClick={() => setSelectedPuesto({ puesto_id: puesto.id, puesto_name: puesto.name, puesto_description: puesto.description || '' })} disabled={isSubmitting}>
                      {puesto.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Estado</label>
              <div className="mp-form-chips">
                <button type="button" className={`mp-form-chip ${isActive ? 'selected' : ''}`} onClick={() => setIsActive(true)} disabled={isSubmitting}>Activo</button>
                <button type="button" className={`mp-form-chip ${!isActive ? 'selected' : ''}`} onClick={() => setIsActive(false)} disabled={isSubmitting}>Inactivo</button>
              </div>
            </div>

            <div className="mp-summary-card">
              <h4>Resumen</h4>
              <div className="mp-summary-item"><span className="label">Nombre</span><span className="value">{formValues.username} {formValues.last_name}</span></div>
              <div className="mp-summary-item"><span className="label">Email</span><span className="value">{formValues.email}</span></div>
              {selectedRol && <div className="mp-summary-item"><span className="label">Rol</span><span className="value">{selectedRol.rol_name}</span></div>}
              {selectedPuesto && <div className="mp-summary-item"><span className="label">Puesto</span><span className="value">{selectedPuesto.puesto_name}</span></div>}
              <div className="mp-summary-item"><span className="label">Estado</span><span className="value">{isActive ? 'Activo' : 'Inactivo'}</span></div>
            </div>
          </>
        )}
      </div>

      <div className="mp-modal-footer">
        {currentStep === 1 ? (
          <>
            <button className="mp-modal-btn-cancel" onClick={handleClose} disabled={isSubmitting}>Cancelar</button>
            <button className="mp-modal-btn-submit" onClick={handleNextStep} disabled={isSubmitting}>
              Siguiente <HiOutlineChevronRight size={16} />
            </button>
          </>
        ) : (
          <>
            <button className="mp-modal-btn-cancel" onClick={handlePrevStep} disabled={isSubmitting}>
              <HiOutlineChevronLeft size={16} /> Atrás
            </button>
            <button className="mp-modal-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Actualizando...' : (<><HiOutlinePencilSquare size={16} /> Actualizar</>)}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EditUser;
